import { Form } from '@homex/se-react-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import {
  setCurrentCarouselItem,
  setCustomerLocationPhone,
  setFirstName,
  setLastName,
  setServiceAddress,
} from 'actions';
import { CarouselBottomButton } from 'components/CarouselBottomButton/CarouselBottomButton';
import { Phrase } from 'components/Phrase';
import { RadioButton } from 'components/RadioButton';
import { useAutoAdvanceEffects } from 'hooks/useAutoAdvanceEffects';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselReduxReducer,
  IAddressInput,
  ICarouselItemCustomerLocationsBottomBaseProps,
  IState,
} from 'typings';

import { Container, Description, FormGroup, List, Subhead } from './styles';

export const CarouselItemCustomerLocationsBottomBase = ({
  allowLocationsNotInServiceZoneStateProp = 'customerLookupAllowLocationsNotInServiceZone',
  customerLocationsStateProp = 'customerLookupCustomerLocations',
  descriptionPhraseId = 'CUSTOMER_LOCATIONS.DESCRIPTION',
  existingLocationDestinationId,
  nextDestinationId,
  parentCarouselItemId,
  previousCustomerDestinationId,
  radioButtonPhraseId = 'CUSTOMER_LOCATIONS.NEW_ADDRESS.BUTTON',
  subheadNoValidLocationsPhraseId = 'CUSTOMER_LOCATIONS.SUBHEAD.NO_VALID_LOCATIONS.HTML',
  subheadPhraseId = 'CUSTOMER_LOCATIONS.SUBHEAD',
}: ICarouselItemCustomerLocationsBottomBaseProps) => {
  const radioGroupName = 'CarouselItemCustomerLocationsRadioButton';
  const newSelectedLocationId = 'NEW';

  const dispatch = useThunkDispatch();
  const {
    allowLocationsNotInServiceZone,
    customerLocations,
    firstName,
    flow,
    isPreviousCustomer,
  } = useSelector((state: IState) => ({
    allowLocationsNotInServiceZone:
      state.userInput[allowLocationsNotInServiceZoneStateProp],
    customerLocations: state.userInput[customerLocationsStateProp],
    firstName: state.userInput.firstName,
    flow: state.client.flow,
    isPreviousCustomer: state.userInput.isPreviousCustomer,
  }));

  const { buttonType } = useTheme();

  const bottomButtonFormat = flow?.buttonType ?? buttonType;

  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isContinueDisabled = !selectedLocationId;

  const validCustomerLocations = allowLocationsNotInServiceZone
    ? customerLocations
    : customerLocations.filter(({ isInServiceZone }) => isInServiceZone);

  const handleRadioChange = (event: React.FormEvent<HTMLInputElement>) => {
    setSelectedLocationId(event.currentTarget.value);
  };

  const handleContinueClick = useCallback(async () => {
    const correctedCustomerLocation = customerLocations.find(
      ({ location: { id } }) => id === selectedLocationId,
    );

    if (correctedCustomerLocation) {
      const { customer, location } = correctedCustomerLocation;
      const {
        contact: { firstName: correctedFirstName, lastName },
        phone,
      } = customer;
      const address: IAddressInput = {
        city: location.city,
        id: location.addressId,
        state: location.state,
        street1: location.street1,
        timezone: location.timezone,
        zipCode: location.zipCode,
      };

      if (location.street2) {
        address.street2 = location.street2;
      }

      dispatch(setCustomerLocationPhone(phone));
      dispatch(setServiceAddress(address));
      dispatch(setFirstName(correctedFirstName));
      dispatch(setLastName(lastName));
      dispatch(
        setCurrentCarouselItem(
          CarouselReduxReducer.SERVICE,
          existingLocationDestinationId,
        ),
      );
    } else if (isPreviousCustomer) {
      dispatch(
        setCurrentCarouselItem(
          CarouselReduxReducer.SERVICE,
          previousCustomerDestinationId,
        ),
      );
    } else {
      dispatch(
        setCurrentCarouselItem(CarouselReduxReducer.SERVICE, nextDestinationId),
      );
    }
  }, [
    customerLocations,
    dispatch,
    existingLocationDestinationId,
    previousCustomerDestinationId,
    nextDestinationId,
    isPreviousCustomer,
    selectedLocationId,
  ]);

  useEffect(() => {
    dispatch(setCustomerLocationPhone({}));
  }, [dispatch]);

  useAutoAdvanceEffects(
    !isContinueDisabled,
    useCallback(() => {
      setIsLoading(true);
    }, []),
    useCallback(() => {
      setIsLoading(false);
      handleContinueClick();
    }, [handleContinueClick]),
  );

  return (
    <FormGroup>
      <Container>
        <Description>
          <Phrase FIRST_NAME={firstName || ''} id={descriptionPhraseId} />
        </Description>
        <Subhead>
          {validCustomerLocations.length > 0 ? (
            <Phrase id={subheadPhraseId} />
          ) : (
            <Phrase id={subheadNoValidLocationsPhraseId} />
          )}
        </Subhead>
        <Form>
          {validCustomerLocations.map(({ location: { id, street1 } }) => (
            <List key={id}>
              <RadioButton
                checked={selectedLocationId === id}
                name={radioGroupName}
                onChange={handleRadioChange}
                value={id}
              >
                {street1} <br />
              </RadioButton>
            </List>
          ))}
          <List>
            <RadioButton
              checked={selectedLocationId === newSelectedLocationId}
              name={radioGroupName}
              onChange={handleRadioChange}
              value={newSelectedLocationId}
            >
              <Phrase id={radioButtonPhraseId} />
            </RadioButton>
          </List>
        </Form>
      </Container>
      <CarouselBottomButton
        buttonFormat={bottomButtonFormat}
        carouselItemId={parentCarouselItemId}
        isDisabled={isContinueDisabled}
        isLoading={isLoading}
        onContinueClick={handleContinueClick}
      />
    </FormGroup>
  );
};
